import { useState } from 'react';

import { Modal } from '@/components/Modal';
import { SignaturePad } from '@/components/SignaturePad';
import { StampPicker } from '@/components/StampPicker';
import { confirmSigningOtp, fetchSigningFile, startSigningOtp, useSignContract } from '@/lib/contractSigning';
import { millionWon } from '@/lib/format';
import type { ContractSigning, Uuid, VerificationChallenge } from '@/lib/types';

/**
 * 계약서 서명 — 한 창에서 세 걸음 (V625 · DOC-98 §11-3).
 *
 * <ol>
 *   <li>계약서 — 원본을 열어 보고 「확인했습니다」.
 *   <li>본인확인 — 계정에 등록된 휴대폰으로 카카오 알림톡 인증번호. 번호를 여기서 적지 않는다.
 *   <li>서명 — 그린다.
 * </ol>
 *
 * <p>[서명] 은 늘 서 있다(머리 줄). 빠진 걸음이 있으면 누를 때 그 걸음을 말한다.
 */
export function SignContractModal({
  siteId,
  signing,
  onClose,
  onSigned,
}: {
  siteId: Uuid;
  signing: ContractSigning;
  onClose: () => void;
  onSigned: () => void;
}) {
  const sign = useSignContract(siteId);
  const [read, setRead] = useState(false);
  const [challenge, setChallenge] = useState<VerificationChallenge | null>(null);
  const [code, setCode] = useState('');
  const [ticket, setTicket] = useState<string | null>(null);
  const [signature, setSignature] = useState<string | null>(null);
  /* 서명을 그리거나 도장을 올린다(V633). 둘은 따로 들고 있다 — 바꿔 눌러도 앞의 것이 지워지지 않는다. */
  const [mark, setMark] = useState<'SIGN' | 'SEAL'>('SIGN');
  const [stamp, setStamp] = useState<string | null>(null);
  const chosen = mark === 'SEAL' ? stamp : signature;
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const fail = (cause: unknown) => setMessage(cause instanceof Error ? cause.message : '처리하지 못했습니다.');

  const openOriginal = () => {
    fetchSigningFile(siteId, signing.id, 'original', false)
      .then(({ url }) => {
        window.open(url, '_blank', 'noopener');
        setRead(true);
      })
      .catch(fail);
  };

  const sendCode = () => {
    setMessage(null);
    setBusy(true);
    startSigningOtp(siteId, signing.id)
      .then((sent) => {
        setChallenge(sent);
        setCode('');
        setTicket(null);
      })
      .catch(fail)
      .finally(() => setBusy(false));
  };

  const checkCode = () => {
    if (!challenge) return;
    setMessage(null);
    setBusy(true);
    confirmSigningOtp(siteId, signing.id, challenge.verificationId, code)
      .then(({ ticket: issued }) => setTicket(issued))
      .catch(fail)
      .finally(() => setBusy(false));
  };

  const submit = () => {
    if (!read) return setMessage('문서를 열어 확인해 주세요.');
    if (!ticket) return setMessage('인증번호 확인을 먼저 해 주세요.');
    if (!chosen) return setMessage(mark === 'SEAL' ? '도장을 올려 주세요.' : '서명을 그려 주세요.');
    setMessage(null);
    sign.mutate({ signingId: signing.id, ticket, signature: chosen, mark }, { onSuccess: onSigned, onError: fail });
  };

  const title = `${signing.title || '하도급계약서'} · ${[signing.trade, signing.workName].filter(Boolean).join(' · ')}`;

  return (
    <Modal
      title={title}
      onClose={onClose}
      maxWidth="32rem"
      actions={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            닫기
          </button>
          <button type="button" className="btn-primary" onClick={submit} disabled={sign.isPending}>
            {sign.isPending ? '서명하는 중…' : '서명'}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <section className="flex flex-col gap-2">
          <Step no={1} label="문서" done={read} />
          <div className="text-ui-xs" style={{ color: 'var(--text-secondary)' }}>
            {signing.originalName ?? '문서'}
            {signing.contractNo && ` · ${signing.contractNo}`}
            {signing.amountWithVat !== null && ` · ${millionWon(signing.amountWithVat)}`}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" className="btn-secondary" onClick={openOriginal}>
              문서 보기
            </button>
            <label className="flex items-center gap-1 text-ui-xs">
              <input type="checkbox" checked={read} onChange={(event) => setRead(event.target.checked)} />
              내용을 확인했습니다
            </label>
          </div>
        </section>

        <section className="flex flex-col gap-2">
          <Step no={2} label="본인확인" done={ticket !== null} />
          {ticket ? (
            <div className="text-ui-xs">{challenge?.maskedTarget} 인증을 마쳤습니다.</div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <button type="button" className="btn-secondary" onClick={sendCode} disabled={busy}>
                  {challenge ? '인증번호 다시 받기' : '인증번호 받기'}
                </button>
                {challenge && (
                  <span className="text-ui-xs" style={{ color: 'var(--text-secondary)' }}>
                    {challenge.maskedTarget} 로 보냈습니다
                  </span>
                )}
              </div>
              {challenge && (
                <div className="flex items-center gap-2">
                  <input
                    className="input-field"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="인증번호"
                    value={code}
                    onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 8))}
                    style={{ maxWidth: '10rem' }}
                  />
                  <button type="button" className="btn-secondary" onClick={checkCode} disabled={busy || code.length === 0}>
                    확인
                  </button>
                </div>
              )}
            </>
          )}
        </section>

        <section className="flex flex-col gap-2">
          <Step no={3} label="서명 · 도장" done={chosen !== null} />
          <div className="flex gap-2">
            <button type="button" className={mark === 'SIGN' ? 'btn-primary' : 'btn-secondary'} onClick={() => setMark('SIGN')}>
              서명 그리기
            </button>
            <button type="button" className={mark === 'SEAL' ? 'btn-primary' : 'btn-secondary'} onClick={() => setMark('SEAL')}>
              도장 올리기
            </button>
          </div>
          {/* 둘 다 그려 둔 채 하나만 보인다 — 서명 판을 내렸다 올리면 그린 선이 화면에서 사라진다. */}
          <div style={{ display: mark === 'SIGN' ? 'block' : 'none' }}>
            <SignaturePad value={signature} onChange={setSignature} />
          </div>
          <div style={{ display: mark === 'SEAL' ? 'block' : 'none' }}>
            <StampPicker value={stamp} onChange={setStamp} />
          </div>
        </section>

        {message && (
          <div className="text-ui-xs" style={{ color: 'var(--danger, #dc2626)' }}>
            {message}
          </div>
        )}
      </div>
    </Modal>
  );
}

function Step({ no, label, done }: { no: number; label: string; done: boolean }) {
  return (
    <div className="flex items-center gap-2 text-ui-sm font-semibold">
      <span
        className="inline-flex h-5 w-5 items-center justify-center rounded-full text-ui-2xs"
        style={{ background: done ? '#16a34a' : 'var(--bg-muted, #e2e8f0)', color: done ? '#fff' : 'var(--text-secondary)' }}
      >
        {done ? '✓' : no}
      </span>
      {label}
    </div>
  );
}
